import * as RadixToast from "@radix-ui/react-toast";
import { useCallback, useMemo, useState, type ReactNode } from "react";

import { IconButton } from "./Button";
import { ToastContext, type ToastApi, type ToastTone } from "./ToastContext";
import { Alert, Close, Info, Success } from "./icons";
import { cn } from "./cn";

interface ToastRecord {
  id: number;
  tone: ToastTone;
  message: string;
}

const TONE: Record<ToastTone, { className: string; icon: typeof Info }> = {
  success: { className: "bg-success-bg text-success", icon: Success },
  error: { className: "bg-error-bg text-error", icon: Alert },
  info: { className: "bg-info-bg text-info", icon: Info },
};

/** Auto-dismiss after 4s — except errors, which wait for the reader. */
const AUTO_DISMISS_MS = 4000;

export function ToastProvider({
  children,
  closeLabel = "Close",
}: {
  children: ReactNode;
  closeLabel?: string;
}) {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);
  const show = useCallback((message: string, tone: ToastTone = "info") => {
    setToasts((current) => [
      ...current,
      { id: Date.now() + current.length, tone, message },
    ]);
  }, []);
  const api = useMemo<ToastApi>(() => ({ show }), [show]);
  return (
    <ToastContext.Provider value={api}>
      <RadixToast.Provider duration={AUTO_DISMISS_MS} swipeDirection="right">
        {children}
        {toasts.map((toast) => {
          const { className, icon: Glyph } = TONE[toast.tone];
          return (
            <RadixToast.Root
              key={toast.id}
              duration={toast.tone === "error" ? Infinity : AUTO_DISMISS_MS}
              onOpenChange={(open) => {
                if (!open)
                  setToasts((current) =>
                    current.filter((item) => item.id !== toast.id),
                  );
              }}
              className={cn(
                "flex items-center gap-3 rounded-[20px] px-4 py-3 shadow-card",
                className,
              )}
            >
              <Glyph size={18} className="shrink-0" />
              <RadixToast.Title className="flex-1 text-callout font-medium">
                {toast.message}
              </RadixToast.Title>
              <RadixToast.Close asChild>
                <IconButton label={closeLabel} size="sm">
                  <Close size={14} />
                </IconButton>
              </RadixToast.Close>
            </RadixToast.Root>
          );
        })}
        <RadixToast.Viewport
          data-print="hidden"
          className="fixed bottom-[calc(env(safe-area-inset-bottom)+var(--tabbar-h)+1rem)] right-gutter z-50 flex w-[min(22rem,calc(100vw-2*var(--page-gutter)))] flex-col gap-2 outline-none"
        />
      </RadixToast.Provider>
    </ToastContext.Provider>
  );
}
